import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { FaBookOpen, FaUsers, FaChalkboardTeacher, FaGlobe, FaTrophy, FaAward, FaLinkedin, FaTwitter } from 'react-icons/fa';
import './About.css';

const About = () => {
  const [showContactForm, setShowContactForm] = useState(false);
  const formRef = useRef();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await emailjs.sendForm(
        'service_r4rdqel',
        'template_2nemcjs',
        formRef.current,
        'r4h-QQHVf4PaVzNiV'
      );
      alert('Message sent successfully!');
      setShowContactForm(false);
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="about-container">
      {/* Hero Section with Animated Background */}
      <motion.div 
        className="about-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        style={{ backgroundImage: `url(https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3)` }}
      >
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>About Our GSK College</h1>
          <p>Building Future Leaders Through Quality Education</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowContactForm(true)}
            className="cta-button"
          >
            Contact Us
          </motion.button>
        </div>
      </motion.div>

      <div className="about-grid">
        <motion.div 
          className="about-image"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <img src="/assets/campus1.jpg" alt="School Building" />
        </motion.div>
        <motion.div 
          className="about-content"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <h2>Our Vision</h2>
          <p>हमारा उद्देश्य एक ऐसा कंप्यूटर इंस्टीट्यूट बनाना है 
            जो तकनीकी शिक्षा के क्षेत्र में उत्कृष्टता का प्रतीक हो। 
            हम चाहते हैं कि हर विद्यार्थी को आधुनिक डिजिटल कौशल से सुसज्जित किया जाए 
            ताकि वह तेजी से बदलती
             तकनीकी दुनिया में आत्मनिर्भर और सफल बन सके।</p>
        </motion.div>
      </div>

      {/* Mission & Vision Section */}
      <motion.section 
        className="mission-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
      >
        <div className="mission-content">
          <motion.h2
            initial={{ x: -50, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            Our Mission
          </motion.h2>
          <motion.p
            initial={{ x: -50, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            To nurture young minds and empower students with knowledge, skills, and values to become responsible global citizens and future leaders.
          </motion.p>
        </div>
        <motion.div 
          className="mission-image"
          initial={{ x: 50, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <img src="https://images.unsplash.com/photo-1503676382389-4809596d5290?w=800&auto=format&fit=crop&q=60" alt="Our Mission" />
        </motion.div>
      </motion.section>

      <motion.section 
        className="vision-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
      >
        <div className="vision-content">
          <motion.h2
            initial={{ x: 50, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            Our Vision
          </motion.h2>
          <motion.p
            initial={{ x: 50, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            To be a leading institution recognized for academic excellence, innovation, and holistic development of students.
          </motion.p>
        </div>
        <motion.div 
          className="vision-image"
          initial={{ x: -50, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <img src="https://images.unsplash.com/photo-1464983953574-0892a716854b?w=800&auto=format&fit=crop&q=60" alt="Our Vision" />
        </motion.div>
      </motion.section>

      {/* Principal's Message */}
      <motion.section 
        className="principal-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
      >
        <div className="principal-image">
          <img src="/assets/gsk director.jpg" alt="Principal" />
        </div>
        <div className="principal-message">
          <h2>Director's Message</h2>
          <p>
            "Welcome to our College! We believe in fostering a nurturing environment where every child can thrive academically, socially, and emotionally. Our dedicated faculty and staff are committed to guiding students on their journey of lifelong learning and success."
          </p>
          <span>- Er.MANOJ KUMAR KUSHWAHA</span>
        </div>
      </motion.section>

      {/* Core Values Section */}
      <section className="values-section">
        <motion.h2
          initial={{ y: -20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          Our Core Values
        </motion.h2>
        <motion.div 
          className="values-grid"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div 
            className="value-card"
            whileHover={{ scale: 1.05, y: -10 }}
            variants={fadeIn}
          >
            <div className="value-icon-wrapper">
              <FaBookOpen className="value-icon" />
            </div>
            <h3>Academic Excellence</h3>
            <p>Striving for the highest standards in teaching and learning.</p>
          </motion.div>

          <motion.div 
            className="value-card"
            whileHover={{ scale: 1.05, y: -10 }}
            variants={fadeIn}
          >
            <div className="value-icon-wrapper">
              <FaUsers className="value-icon" />
            </div>
            <h3>Integrity</h3>
            <p>Upholding honesty, respect, and responsibility in all actions.</p>
          </motion.div>

          <motion.div 
            className="value-card"
            whileHover={{ scale: 1.05, y: -10 }}
            variants={fadeIn}
          >
            <div className="value-icon-wrapper">
              <FaChalkboardTeacher className="value-icon" />
            </div>
            <h3>Holistic Growth</h3>
            <p>Encouraging all-round development through academics and co-curriculars.</p>
          </motion.div>

          <motion.div 
            className="value-card"
            whileHover={{ scale: 1.05, y: -10 }}
            variants={fadeIn}
          >
            <div className="value-icon-wrapper">
              <FaGlobe className="value-icon" />
            </div>
            <h3>Global Citizenship</h3>
            <p>Preparing students to contribute positively to the world.</p>
          </motion.div>
        </motion.div>
      </section>

      {/* Achievements Section */}
      <motion.section 
        className="achievements-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
      >
        <h2>Our Achievements</h2>
        <div className="achievements-grid">
          <motion.div 
            className="achievement-card"
            whileHover={{ scale: 1.05 }}
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <FaTrophy className="achievement-icon" />
            <h3>100+</h3>
            <p>Academic Awards</p>
          </motion.div>

          <motion.div 
            className="achievement-card"
            whileHover={{ scale: 1.05 }}
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <FaAward className="achievement-icon" />
            <h3>50+</h3>
            <p>Sports & Cultural Trophies</p>
          </motion.div>

          <motion.div 
            className="achievement-card"
            whileHover={{ scale: 1.05 }}
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <FaGlobe className="achievement-icon" />
            <h3>10+</h3>
            <p>International Collaborations</p>
          </motion.div>
        </div>
      </motion.section>

      {/* Faculty/Team Section */}
      <motion.section 
        className="team-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
      >
        <h2>Our Faculty</h2>
        <div className="team-grid">
          <motion.div 
            className="team-member"
            whileHover={{ y: -10 }}
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="member-image">
              <img src="/assets/my2.jpg" alt="Faculty" />
              <div className="member-overlay">
                <div className="social-links">
                  <a href="https://www.linkedin.com/in/abhishek-prajapati-09a455271/"><FaLinkedin /></a>
                  <a href="#"><FaTwitter /></a>
                </div>
              </div>
            </div>
            <h3>Er.Abhishek sir</h3>
            <p>Computer Science </p>
          </motion.div>

          <motion.div 
            className="team-member"
            whileHover={{ y: -10 }}
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="member-image">
              <img src="/assets/farhaan.jpg" alt="Faculty" />
              <div className="member-overlay">
                <div className="social-links">
                  <a href="#"><FaLinkedin /></a>
                  <a href="#"><FaTwitter /></a>
                </div>
              </div>
            </div>
            <h3>Mr. Farhan sir</h3>
            <p>Science Department Head</p>
          </motion.div>

          <motion.div 
            className="team-member"
            whileHover={{ y: -10 }}
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="member-image">
              <img src="/assets/raj.jpg" alt="Faculty" />
              <div className="member-overlay">
                <div className="social-links">
                  <a href="#"><FaLinkedin /></a>
                  <a href="#"><FaTwitter /></a>
                </div>
              </div>
            </div>
            <h3>Mr.Raj sir</h3>
            <p>English & Computer Language Expert & IT Coordinator</p>
          </motion.div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.div 
        className="cta-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
      >
        <div className="cta-content">
          <h2>Be a Part of Our GSK Family</h2>
          <p>Join us in our mission to shape bright futures and responsible citizens.</p>
          <motion.button 
            className="cta-button"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowContactForm(true)}
          >
            Contact Us
          </motion.button>
        </div>
        <div className="cta-particles">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="particle"
              animate={{
                y: [0, -100],
                opacity: [0, 1, 0],
                scale: [0, 1, 0]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.1
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Contact Form Modal */}
      {showContactForm && (
        <motion.div 
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div 
            className="contact-modal"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            <form ref={formRef} onSubmit={handleSubmit}>
              <h2>Contact Us</h2>
              <div className="form-group">
                <input type="text" name="name" placeholder="Your Full Name*" required />
              </div>
              <div className="form-group">
                <input type="email" name="email" placeholder="Email Address*" required />
              </div>
              <div className="form-group">
                <input type="tel" name="phone" placeholder="Phone Number*" required />
              </div>
              <div className="form-group">
                <select name="subject" required>
                  <option value="">Select Subject*</option>
                  <option value="admission">Admission Inquiry</option>
                  <option value="general">General Query</option>
                  <option value="feedback">Feedback</option>
                </select>
              </div>
              <div className="form-group">
                <textarea name="message" placeholder="Your Message*" rows="4" required></textarea>
              </div>
              <button type="submit" className="submit-button">Send Message</button>
            </form>
            <button className="close-modal" onClick={() => setShowContactForm(false)}>&times;</button>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default About;